import { loginWithEmail, loginWithGoogle } from '@/api/auth';
import {
  persistLoginResponse,
  getWalletAddress,
  getUserId,
  hasAuthSession,
  getOriginLoginType,
  setLoginWalletProviderId,
  setOriginLoginType,
  setWalletAddress,
  setWalletProviderId,
} from '@/utils/auth-token';
import { getAuthGeneration, registerAuthTeardown } from '@/utils/auth-teardown';
import { completePrivyBackendBindAfterLogin } from '@/utils/privy-bind';
import {
  ensurePrivyAuthReady,
  linkPlatformCustomAuthToRegisteredWallet,
  syncPrivyAfterPlatformLogin,
} from '@/utils/privy';
import { listTradeAddresses } from '@/api/trade';
import { hasCustodialTradeAddress, pickWalletFromTradeAddressList } from '@/utils/trade-address';

/** @type {Promise<{ ok: boolean, preparing?: boolean }>|null} */
let pendingSilentBind = null;

/** 静默 bind 硬失败后抑制一次游客福利弹窗（历史路径保留消费方） */
let suppressGuestPromptAfterAuthFailure = false;

function currentAuthSession() {
  return {
    generation: getAuthGeneration(),
    userId: String(getUserId() ?? '').trim(),
  };
}

function isAuthSessionCurrent({ generation, userId }) {
  const current = currentAuthSession();
  return current.generation === generation && current.userId === userId && hasAuthSession();
}

function assertAuthSessionCurrent(session) {
  if (isAuthSessionCurrent(session)) return;
  const error = new Error('AUTH_SESSION_CHANGED');
  error.code = 'AUTH_SESSION_CHANGED';
  throw error;
}

/**
 * App 登出 watch：消费「bind 失败刚踢下线」标记，跳过游客福利窗。
 * @returns {boolean}
 */
export function consumeSuppressGuestPromptAfterAuthFailure() {
  if (!suppressGuestPromptAfterAuthFailure) return false;
  suppressGuestPromptAfterAuthFailure = false;
  return true;
}

/**
 * 已有 type=2 时回填本地 provider/address，供 canShowDeposit 与充提入口使用。
 * @returns {Promise<boolean>} 是否确认存在托管地址
 */
export async function syncCustodialSessionFromTradeAddresses() {
  const session = currentAuthSession();
  try {
    const raw = await listTradeAddresses();
    if (!isAuthSessionCurrent(session)) return false;
    const records = Array.isArray(raw) ? raw : [];
    if (!hasCustodialTradeAddress(records)) return false;
    setWalletProviderId('privy');
    const address = pickWalletFromTradeAddressList(records);
    if (address) setWalletAddress(address);
    return true;
  } catch (err) {
    console.warn('[auth] trade-address list failed while syncing custodial session', err);
    return false;
  }
}

/** 平台 Session 下 list 是否已有 type=2（老号无需再走 Privy SDK） */
async function platformHasCustodialTradeAddress() {
  return syncCustodialSessionFromTradeAddresses();
}

/**
 * 平台登录成功后：Custom JWT → Privy custom_auth → App Signer + trade-address/add type=2。
 * 禁止再调 /login/wallet（会以嵌入式地址新建 WALLET 孤儿账户）。
 * PC / App 对齐：以平台 user_id 为 Privy DID，避免 google_oauth 与 custom_auth 双钱包。
 * 已有 type=2 时跳过 Privy（避免本机 SDK/iframe 失败把刚登录的 Session 踢掉），并回填本地钱包态。
 */
export async function completePrivyAfterPlatformLogin({ originLoginType, email, inviteCode } = {}) {
  const session = currentAuthSession();
  assertAuthSessionCurrent(session);
  if (await platformHasCustodialTradeAddress()) {
    // 旧账号首次在仍保留原 Privy 会话的浏览器登录时，把平台 custom_auth
    // 链接到原资金钱包。失败不阻断登录，也绝不切换/覆盖登记地址。
    const registeredAddress = String(getWalletAddress() ?? '').trim();
    if (registeredAddress) {
      void linkPlatformCustomAuthToRegisteredWallet(registeredAddress).catch((error) => {
        console.warn('[auth] legacy Privy identity bridge failed', error);
      });
    }
    return null;
  }
  // 付款后服务端 Ensure 可能仍在写库：再轮询 list，命中则只水合，禁止 SDK create。
  for (const waitMs of [800, 1600, 3200]) {
    assertAuthSessionCurrent(session);
    await new Promise((r) => setTimeout(r, waitMs));
    if (await syncCustodialSessionFromTradeAddresses()) {
      return null;
    }
  }
  assertAuthSessionCurrent(session);
  await ensurePrivyAuthReady();
  assertAuthSessionCurrent(session);
  const { user } = await syncPrivyAfterPlatformLogin();
  assertAuthSessionCurrent(session);
  await completePrivyBackendBindAfterLogin(user, {
    originLoginType,
    email,
    inviteCode,
  });
  assertAuthSessionCurrent(session);
  return user;
}

/**
 * 邮箱验证码 → 仅平台 Session（验证通过即可关登录窗）。
 */
export async function loginEmailPlatform({ email, code, inviteCode, captchaToken } = {}) {
  const data = await loginWithEmail({
    email,
    code,
    inviteCode,
    captchaToken,
    originLoginType: 'EMAIL',
  });
  persistLoginResponse(data);
  setOriginLoginType('EMAIL');
  setLoginWalletProviderId('');
  return data;
}

/**
 * Google idToken → 仅平台 Session。
 * @param {{ googleToken: string, inviteCode?: string, email?: string, captchaToken?: string, serverTicket?: string, pow?: object, botProof?: string }} [opts]
 */
export async function loginGooglePlatform({
  googleToken,
  inviteCode,
  email,
  captchaToken,
  serverTicket,
  pow,
  botProof,
} = {}) {
  const data = await loginWithGoogle({
    googleToken,
    inviteCode,
    captchaToken,
    serverTicket,
    pow,
    botProof,
    originLoginType: 'GOOGLE',
  });
  persistLoginResponse(data);
  setOriginLoginType('GOOGLE');
  setLoginWalletProviderId('');
  return {
    data,
    email:
      String(email ?? '').trim() ||
      String(data?.email ?? data?.userEmail ?? data?.user?.email ?? '').trim() ||
      undefined,
  };
}

/** 关窗后静默 bind 是否仍在进行（半登录期资金操作应拦截） */
export function isSilentCustodialBindPending() {
  return Boolean(pendingSilentBind);
}

/**
 * App 登录后 bootstrap：等静默托管绑定结束（无 pending 则立刻 resolve）。
 * @returns {Promise<{ ok: boolean, preparing?: boolean }>}
 */
export function awaitPendingSilentCustodialBind() {
  const p = pendingSilentBind;
  if (!p) return Promise.resolve({ ok: true });
  return p;
}

/**
 * 关窗后静默：Privy sync + trade-address 绑定。
 * App Signer 失败时有限重试，保持登录，后续资金操作可再次静默恢复；
 * Privy 硬失败保留平台 Session（勿踢下线），已有 type=2 视为成功。
 *
 * 2026-09-06：邮箱/Google 登录成功后不再调用本函数；改为付款/入账后
 * {@link scheduleSilentPrivyBindAfterFirstDeposit}（含 MixPay SUCCESS 未 CREDITED），
 * 避免机器人刷注册产生 Privy 钱包费用。
 */
export function scheduleSilentPrivyBindAfterPlatformLogin({
  originLoginType,
  email,
  inviteCode,
} = {}) {
  const origin = String(originLoginType || '')
    .trim()
    .toUpperCase();

  if (pendingSilentBind) return pendingSilentBind;
  const task = (async () => {
    // 登录调用方会在 schedule 返回后同步 bump authGeneration；下一微任务再取稳定代次。
    await Promise.resolve();
    const session = currentAuthSession();
    const maxAttempts = 3;
    let lastErr = null;
    for (let i = 0; i < maxAttempts; i += 1) {
      if (!isAuthSessionCurrent(session)) return { ok: false };
      try {
        if (i > 0) {
          await new Promise((r) => setTimeout(r, 1000 * 2 ** (i - 1)));
        }
        if (!isAuthSessionCurrent(session)) return { ok: false };
        await completePrivyAfterPlatformLogin({
          originLoginType: origin || undefined,
          email,
          inviteCode,
        });
        if (!isAuthSessionCurrent(session)) return { ok: false };
        return { ok: true };
      } catch (err) {
        lastErr = err;
        if (isPrivyDepositRequiredError(err)) {
          console.info('[auth] custodial bind deferred until first deposit', err?.message || err);
          return { ok: false, deferredUntilDeposit: true };
        }
        if (!isAuthSessionCurrent(session)) return { ok: false };
        if (err?.bindResult?.prepared) {
          // Do not open a manual signer dialog or treat a failed grant as ready.
          console.warn('[auth] silent App Signer grant pending', i + 1, err);
          continue;
        }
        // 平台 Session 已落盘：Privy SDK 失败时勿踢下线（老号多半已有 type=2；新号仍可浏览）
        if (await platformHasCustodialTradeAddress()) {
          console.warn('[auth] silent custodial bind failed but type=2 exists; keep session', err);
          return { ok: true };
        }
        console.warn('[auth] silent custodial bind attempt failed', i + 1, err);
      }
    }
    console.warn(
      '[auth] silent custodial bind failed after retries; keep platform session',
      lastErr
    );
    return { ok: false, preparing: true };
  })();

  pendingSilentBind = task;
  task.finally(() => {
    if (pendingSilentBind === task) {
      pendingSilentBind = null;
    }
  });
  return task;
}

/** 后端 custom-token / 绑定拒绝：尚未真实充值（业务码 818，勿把任意 403 当成闸） */
export function isPrivyDepositRequiredError(err) {
  const code = String(err?.code ?? '').trim();
  if (code === 'PRIVY_DEPOSIT_REQUIRED' || code === '818') return true;
  if (Number(err?.code) === 818) return true;
  const raw = String(err?.message ?? err?.msg ?? err ?? '');
  return /首次充值|开通交易钱包|privy_deposit_required|PRIVY_DEPOSIT_REQUIRED|\(818\)/i.test(raw);
}

/**
 * 首次付款/入账后：为 EMAIL/GOOGLE 静默开通 Privy 托管钱包（type=2）。
 *
 * 2026-09-07：**兜底路径**。主路径由服务端 EnsureCustodialTradeAddress 预建；
 * FE 应先 {@link syncCustodialSessionFromTradeAddresses}，仅平台仍无 type=2 时调用本函数。
 * 已有 type=2 时内部跳过。WALLET 登录轨不调用。
 */
export function scheduleSilentPrivyBindAfterFirstDeposit(opts = {}) {
  if (!hasAuthSession()) return Promise.resolve({ ok: false });
  const origin = String(opts.originLoginType || getOriginLoginType() || '')
    .trim()
    .toUpperCase();
  if (origin === 'WALLET') {
    return Promise.resolve({ ok: true, skipped: true });
  }
  return scheduleSilentPrivyBindAfterPlatformLogin({
    originLoginType: origin || 'EMAIL',
    email: opts.email,
    inviteCode: opts.inviteCode,
    returnStep: opts.returnStep || 'select',
  });
}

registerAuthTeardown(() => {
  pendingSilentBind = null;
  suppressGuestPromptAfterAuthFailure = false;
});

/**
 * @deprecated 同步等待 Privy；登录窗请用 loginEmailPlatform + scheduleSilentPrivyBindAfterPlatformLogin
 */
export async function loginEmailThenPrivy({ email, code, inviteCode } = {}) {
  const data = await loginEmailPlatform({ email, code, inviteCode });
  try {
    await completePrivyAfterPlatformLogin({
      originLoginType: 'EMAIL',
      email,
      inviteCode,
    });
  } catch (err) {
    if (err?.bindResult?.prepared) throw err;
    if (await platformHasCustodialTradeAddress()) {
      console.warn('[auth] Privy bind failed but type=2 exists; keep session', err);
      return data;
    }
    console.warn('[auth] Privy bind failed; keep platform session', err);
    return data;
  }
  return data;
}

/**
 * @deprecated 同步等待 Privy；登录窗请用 loginGooglePlatform + scheduleSilentPrivyBindAfterPlatformLogin
 */
export async function loginGoogleThenPrivy({ googleToken, inviteCode, email } = {}) {
  const { data, email: resolvedEmail } = await loginGooglePlatform({
    googleToken,
    inviteCode,
    email,
  });
  try {
    await completePrivyAfterPlatformLogin({
      originLoginType: 'GOOGLE',
      email: resolvedEmail,
      inviteCode,
    });
  } catch (err) {
    if (err?.bindResult?.prepared) throw err;
    if (await platformHasCustodialTradeAddress()) {
      console.warn('[auth] Privy bind failed but type=2 exists; keep session', err);
      return data;
    }
    console.warn('[auth] Privy bind failed; keep platform session', err);
    return data;
  }
  return data;
}
